import * as Plot from '@observablehq/plot';
import {useEffect, useRef} from 'react';
import {ParsedGraphConfig, YAxisConfig} from '../../types/GraphTypes';

interface GraphLineChartProps {
  chartData: Record<string, number | string | null>[];
  parsedConfig: ParsedGraphConfig;
  colors: string[];
  xLabel: string;
  xDomain: [number | 'dataMin', number | 'dataMax'] | undefined;
  getYDomain: (config: YAxisConfig) => [number | 'dataMin', number | 'dataMax'];
}

export default function GraphLineChart({
  chartData,
  parsedConfig,
  colors,
  xLabel,
  xDomain,
  getYDomain,
}: GraphLineChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const {yAxes, dataSeries} = parsedConfig;

  useEffect(() => {
    if (!containerRef.current || chartData.length === 0) return;

    // Prepare data for Observable Plot
    const plotData = chartData.map((point) => {
      const result: Record<string, number | string> = {x: Number(point.x)};
      dataSeries.forEach((series) => {
        const value = point[series.key];
        if (value !== null && value !== undefined) {
          result[series.key] = Number(value);
        }
      });
      return result;
    });

    // Calculate domains
    const xMin = xDomain?.[0] === 'dataMin' ? undefined : (xDomain?.[0] as number | undefined);
    const xMax = xDomain?.[1] === 'dataMax' ? undefined : (xDomain?.[1] as number | undefined);

    // For multiple Y axes, we'll use the first one for now (Observable Plot doesn't support dual Y axes directly)
    const primaryYAxis = yAxes[0];
    const primaryYDomain = primaryYAxis ? getYDomain(primaryYAxis.config) : undefined;
    const yMin = primaryYDomain?.[0] === 'dataMin' ? undefined : (primaryYDomain?.[0] as number | undefined);
    const yMax = primaryYDomain?.[1] === 'dataMax' ? undefined : (primaryYDomain?.[1] as number | undefined);

    // Build marks for each series
    const marks: Plot.Markish[] = [
      Plot.gridX({stroke: '#e0e0e0', strokeDasharray: '3,3'}),
      Plot.gridY({stroke: '#e0e0e0', strokeDasharray: '3,3'}),
    ];

    // Add line marks for each series
    dataSeries.forEach((series, index) => {
      const color = colors[index % colors.length];
      const seriesData = plotData.filter((d) => d[series.key] !== null && d[series.key] !== undefined);
      marks.push(
        Plot.line(seriesData, {
          x: 'x',
          y: series.key,
          stroke: color,
          strokeWidth: 2,
        }),
        Plot.dot(seriesData, {
          x: 'x',
          y: series.key,
          fill: color,
          r: 4,
        }),
      );
    });

    // Create the plot
    const plotOptions: Plot.PlotOptions = {
      marks,
      x: {
        label: xLabel,
        domain: xMin !== undefined || xMax !== undefined ? [xMin ?? undefined, xMax ?? undefined] : undefined,
        type: 'linear',
      },
      y: {
        label: primaryYAxis?.config.label || 'Y Axis',
        domain: yMin !== undefined || yMax !== undefined ? [yMin ?? undefined, yMax ?? undefined] : undefined,
        type: 'linear',
      },
      marginTop: 5,
      marginRight: 30,
      marginBottom: 20,
      marginLeft: 20,
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
    };

    const chart = Plot.plot(plotOptions);
    const container = containerRef.current;
    container.innerHTML = '';
    container.appendChild(chart);

    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [chartData, parsedConfig, colors, xLabel, xDomain, getYDomain, yAxes, dataSeries]);

  return <div ref={containerRef} data-testid="graph-line-chart" style={{width: '100%', height: '100%'}} />;
}
